import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { useShallow } from 'zustand/shallow';
import { useEditorStore, useElongationStore } from '../../../stores';
import { CreateElongationPreviewMaterial } from '../../../utils/PreviewMaterialsHandler';
import { useToolManager } from '../../../hooks/useToolManager';
import { TOOLS_CONFIG } from '../../../constants/tools';  

// Units: scene uses centimeters along Y. User input is millimeters.
// 1 mm = 0.1 scene units (cm)
const MM_TO_UNIT = 0.1;

const LINE2_WIDTH = 4.0; // px
const RING_COLOR = '#10b981';

const ElongationHandler = () => {
  const [scene, shadedMesh, material, controls, camera, renderer, replaceShadedMesh, ghostModeEnabled] = useEditorStore(
    useShallow((s) => [
      s.sceneRefs.scene,
      s.sceneRefs.shadedMesh,
      s.sceneRefs.material,
      s.sceneRefs.controls,
      s.sceneRefs.camera,
      s.sceneRefs.renderer,
      s.replaceShadedMesh,
      s.ghostModeEnabled,
    ])
  );

  const [enabled, value, previewMesh, setPreviewMesh, isElongationApplied] = useElongationStore(
    useShallow((s) => [s.enabled, s.value, s.previewMesh, s.setPreviewMesh, s.isElongationApplied])
  );

  const { run } = useToolManager();

  // previewMesh is now managed by the store directly
  const basePosRef = useRef(null); // Float32Array snapshot of pristine positions
  const baseGeomRef = useRef(null); // BufferGeometry snapshot
  const lastMeshRef = useRef(null); // to detect shadedMesh changes
  const previewMeshRef = useRef(null); // to detect previewMesh changes
  const originalMaterialRef = useRef(null); // shaded mesh original material for final replace

  const ringGroupRef = useRef(null); // THREE.Group containing ring visuals, positioned in world
  const tcRef = useRef(null); // TransformControls
  const tcHelperRef = useRef(null); // TransformControls helper

  // ---------- Helpers: ring construction (similar to MeasureReductionHandler) ----------
  const tmpA = useRef(new THREE.Vector3()).current;
  const tmpB = useRef(new THREE.Vector3()).current;
  const tmpC = useRef(new THREE.Vector3()).current;
  const tmpAB = useRef(new THREE.Vector3()).current;
  const tmpBC = useRef(new THREE.Vector3()).current;
  const tmpCA = useRef(new THREE.Vector3()).current;

  function ensureRingGroup() {
    if (!scene) return null;
    if (!ringGroupRef.current) {
      const g = new THREE.Group();
      g.name = 'ElongationRingGroup';
      scene.add(g);
      ringGroupRef.current = g;
    }
    return ringGroupRef.current;
  }

  function disposeRingGroup() {
    const g = ringGroupRef.current;
    if (!g) return;
    g.traverse((o) => {
      o.geometry?.dispose?.();
      o.material?.dispose?.();
    });
    g.parent?.remove(g);
    ringGroupRef.current = null;
  }

  function worldPositionAt(attr, i, mw, out) {
    out.set(attr.getX(i), attr.getY(i), attr.getZ(i));
    return out.applyMatrix4(mw);
  }

  function orderLoop(points, tol = 1e-4) {
    if (!points || points.length < 2) return [];
    const rem = points.map((p) => p.clone());
    const loop = [rem.shift()];
    let guard = 0;
    while (rem.length && guard++ < 20000) {
      const last = loop[loop.length - 1];
      let best = 0,
        bestD = Infinity;
      for (let i = 0; i < rem.length; i++) {
        const d = last.distanceToSquared(rem[i]);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      }
      const next = rem.splice(best, 1)[0];
      if (loop.length > 2 && next.distanceTo(loop[0]) < tol) {
        loop.push(loop[0].clone());
        break;
      }
      loop.push(next);
    }
    if (loop.length >= 2 && loop[0].distanceTo(loop[loop.length - 1]) > tol) loop.push(loop[0].clone());
    return loop;
  }

  function buildSectionAtY(yWorld, color) {
    // Always get current mesh from store to avoid stale closures
    const currentMesh = useEditorStore.getState().sceneRefs.shadedMesh;
    if (!currentMesh) return { thin: null, fat: null, centroid: new THREE.Vector3() };

    const pos = currentMesh.geometry.attributes.position;
    const index = currentMesh.geometry.index ? currentMesh.geometry.index.array : null;
    const mw = currentMesh.matrixWorld;
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -yWorld);

    const tris = index ? index.length / 3 : pos.count / 3;
    const segs = [];
    for (let t = 0; t < tris; t++) {
      const i0 = index ? index[t * 3 + 0] : t * 3 + 0;
      const i1 = index ? index[t * 3 + 1] : t * 3 + 1;
      const i2 = index ? index[t * 3 + 2] : t * 3 + 2;
      worldPositionAt(pos, i0, mw, tmpA);
      worldPositionAt(pos, i1, mw, tmpB);
      worldPositionAt(pos, i2, mw, tmpC);
      const d0 = plane.distanceToPoint(tmpA);
      const d1 = plane.distanceToPoint(tmpB);
      const d2 = plane.distanceToPoint(tmpC);
      const pair = [];
      if (Math.sign(d0) !== Math.sign(d1)) {
        const tt = d0 / (d0 - d1);
        pair.push(tmpAB.copy(tmpB).sub(tmpA).multiplyScalar(tt).add(tmpA).clone());
      }
      if (Math.sign(d1) !== Math.sign(d2)) {
        const tt = d1 / (d1 - d2);
        pair.push(tmpBC.copy(tmpC).sub(tmpB).multiplyScalar(tt).add(tmpB).clone());
      }
      if (Math.sign(d2) !== Math.sign(d0)) {
        const tt = d2 / (d2 - d0);
        pair.push(tmpCA.copy(tmpA).sub(tmpC).multiplyScalar(tt).add(tmpC).clone());
      }
      if (pair.length === 2) {
        segs.push(pair[0], pair[1]);
      }
    }

    // centroid in world
    const centroid = new THREE.Vector3();
    for (let i = 0; i < segs.length; i += 2) centroid.add(segs[i]).add(segs[i + 1]);
    const N = Math.max(segs.length, 1);
    centroid.multiplyScalar(1 / N);
    centroid.y = yWorld;

    // thin (pickable) segments — in centroid-local frame
    const thinPos = new Float32Array(segs.length * 3);
    for (let i = 0; i < segs.length; i++) {
      const p = segs[i];
      thinPos[i * 3 + 0] = p.x - centroid.x;
      thinPos[i * 3 + 1] = p.y - centroid.y;
      thinPos[i * 3 + 2] = p.z - centroid.z;
    }
    const gThin = new THREE.BufferGeometry();
    if (segs.length) gThin.setAttribute('position', new THREE.BufferAttribute(thinPos, 3));
    const mThin = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.001, depthTest: false });
    const thin = new THREE.LineSegments(gThin, mThin);
    thin.name = 'ElongationRingLine_thin';
    thin.renderOrder = 9e5;

    // fat (visual) ordered loop
    let fat = null;
    const loop = orderLoop(segs);
    if (loop.length >= 2) {
      const fatPos = new Float32Array(loop.length * 3);
      for (let i = 0; i < loop.length; i++) {
        const p = loop[i];
        fatPos[i * 3 + 0] = p.x - centroid.x;
        fatPos[i * 3 + 1] = p.y - centroid.y;
        fatPos[i * 3 + 2] = p.z - centroid.z;
      }
      const geo = new LineGeometry();
      geo.setPositions(fatPos);
      const mat = new LineMaterial({ color, linewidth: LINE2_WIDTH, transparent: true, opacity: 0.98, depthTest: false });
      const el = renderer?.domElement;
      if (el) mat.resolution.set(el.clientWidth, el.clientHeight);
      fat = new Line2(geo, mat);
      fat.computeLineDistances();
      fat.name = 'ElongationRingLine_fat';
      fat.renderOrder = 9e5;
      fat.frustumCulled = false;
    }

    return { thin, fat, centroid };
  }

  function syncRingAt(yWorld) {
    const grp = ringGroupRef.current;
    // Always get current mesh from store to avoid stale closures
    const currentMesh = useEditorStore.getState().sceneRefs.shadedMesh;
    if (!grp || !currentMesh) return;
    // Dispose old lines
    const oldThin = grp.getObjectByName('ElongationRingLine_thin');
    if (oldThin) {
      oldThin.geometry?.dispose?.();
      oldThin.material?.dispose?.();
      grp.remove(oldThin);
    }
    const oldFat = grp.getObjectByName('ElongationRingLine_fat');
    if (oldFat) {
      oldFat.geometry?.dispose?.();
      oldFat.material?.dispose?.();
      grp.remove(oldFat);
    }

    const built = buildSectionAtY(yWorld, RING_COLOR);
    grp.position.set(built.centroid.x, yWorld, built.centroid.z);
    if (built.thin) grp.add(built.thin);
    if (built.fat) grp.add(built.fat);
    grp.updateMatrixWorld(true);
  }

  // ---------- Preview mesh management ----------
  function ensurePreview() {
    // Only return existing preview if it's based on the same shadedMesh
    if (previewMesh && previewMesh.parent && lastMeshRef.current === shadedMesh) {
      return previewMesh;
    }
    if(previewMeshRef.current && lastMeshRef.current === shadedMesh) {
      return previewMeshRef.current;
    }
    
    if (!scene || !shadedMesh) return null;

    // Base snapshots for stable rebuilds and to survive shadedMesh changes
    // Always update base references when shadedMesh changes to ensure we use the correct mesh
    if (!baseGeomRef.current || lastMeshRef.current !== shadedMesh) {

      baseGeomRef.current = shadedMesh.geometry.clone();
      if (!baseGeomRef.current.attributes.position) return null;
      basePosRef.current = baseGeomRef.current.attributes.position.array.slice();
      lastMeshRef.current = shadedMesh;
      originalMaterialRef.current = material;
    }

    const cloneGeom = baseGeomRef.current.clone();
    const mat = CreateElongationPreviewMaterial(0.6);
    const clone = new THREE.Mesh(cloneGeom, mat);
    clone.name = 'ElongationPreview';
    clone.matrixAutoUpdate = true;
    clone.position.copy(shadedMesh.position);
    clone.quaternion.copy(shadedMesh.quaternion);
    clone.scale.copy(shadedMesh.scale);
    // clone.renderOrder = 2;
    scene.add(clone);

    previewMeshRef.current = clone;
    setPreviewMesh(clone);
    return clone;
  }

  function disposePreview(options = { keepIfReplaced: false }) {
    const p = previewMeshRef.current;
    if (!p) return;
    if (options.keepIfReplaced) {
      // We are turning the preview into the shaded mesh; do not dispose or remove.
      setPreviewMesh(null);
      return;
    }
    if (p.parent) p.parent.remove(p);
    p.geometry?.dispose?.();
    p.material?.dispose?.();
    setPreviewMesh(null);
    previewMeshRef.current = null;
  }

  function computeLocalYBounds(geometry) {
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    const bb = geometry.boundingBox;
    return { minY: bb.min.y, maxY: bb.max.y };
  }

  const initialRingYWorld = useMemo(() => {
    if (!shadedMesh) return 0;
    const { minY, maxY } = computeLocalYBounds(shadedMesh.geometry);
    const centerLocalY = (minY + maxY) * 0.5;
    return shadedMesh.position.y + centerLocalY;
  }, [shadedMesh]);

  function rebuildPreview() {
    // Always get current mesh from store to avoid stale closures
    const currentMesh = useEditorStore.getState().sceneRefs.shadedMesh;
    const p = ensurePreview();
    if (!p || !currentMesh) return;

    const outPos = p.geometry.attributes.position;
    const basePositions = basePosRef.current;
    if (!outPos || !basePositions) return;

    const { minY } = computeLocalYBounds(baseGeomRef.current || currentMesh.geometry);

    // Get latest state directly from store to avoid stale closure issues
    const latestElongationState = useElongationStore.getState();

    // Convert ring position to local Y assuming alignment (scene Y == local Y)
    const ringWorldY = ringGroupRef.current ? ringGroupRef.current.position.y : initialRingYWorld;
    const ringLocalY = ringWorldY - currentMesh.position.y;

    // User-provided elongation in mm -> scene units (cm)
    const mmValue = parseFloat(latestElongationState?.value ?? 0) || 0;
    const deltaUnits = Math.max(0, mmValue) * MM_TO_UNIT;

    const count = outPos.count;

    // Distance from ring to bottom in local units
    const distBottom = Math.max(0, ringLocalY - minY);
    const useScale = distBottom > 1e-6;
    const scale = useScale ? 1 + deltaUnits / distBottom : 1; // stretch below ring

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const bx = basePositions[i3 + 0];
      const by = basePositions[i3 + 1];
      const bz = basePositions[i3 + 2];

      if (by <= ringLocalY) {
        if (useScale) {
          const d = ringLocalY - by; // >= 0
          const ny = ringLocalY - d * scale;
          outPos.setXYZ(i, bx, ny, bz);
        } else {
          // If ring is at/below bottom, shift everything below by constant delta
          outPos.setXYZ(i, bx, by - deltaUnits, bz);
        }
      } else {
        // Above ring: unchanged
        outPos.setXYZ(i, bx, by, bz);
      }
    }

    outPos.needsUpdate = true;
    p.geometry.computeVertexNormals();
    p.geometry.computeBoundingSphere?.();
  }

  // ---------- Effects ----------
  // Handle final apply on component unmount only
  useEffect(() => {
    return () => {
      const latest = useElongationStore.getState();

      if (latest.enabled && latest.value > 0 && latest.previewMesh) {
        run({ toolId: TOOLS_CONFIG.MAIN_PROCESS.ELONGATION.main_elongation_elongation.id, mode: 'apply' }, TOOLS_CONFIG.MAIN_PROCESS.ELONGATION.main_elongation_elongation.id);
        
        // Clear all base references when applying to prevent stacked calculations
        baseGeomRef.current = null;
        basePosRef.current = null;
        lastMeshRef.current = null;
        originalMaterialRef.current = null;
      }
      else{
        disposePreview();
        setPreviewMesh(null);
      }
    };
  }, []);

  // Detect unapply and reset base references for fresh calculation
  useEffect(() => {
    // If elongation was just unapplied, reset base references to recalculate from current mesh
    if (!isElongationApplied && scene && shadedMesh) {
      baseGeomRef.current = null;
      basePosRef.current = null;
      lastMeshRef.current = null;
      originalMaterialRef.current = null;
      
      // Force recalculation on next ensurePreview call
      if (previewMeshRef.current) {
        disposePreview();
      }
    }
  }, [isElongationApplied, scene, shadedMesh]);

  // Create preview and ring on mount / shadedMesh changes
  useEffect(() => {
    if (!scene || !shadedMesh) return;

    // Check if this is a mesh change (not initial mount)
    const isMeshChange = lastMeshRef.current && lastMeshRef.current !== shadedMesh;

    if (isMeshChange) {

      // Complete reset of all base references to force fresh calculation
      baseGeomRef.current = null;
      basePosRef.current = null;
      lastMeshRef.current = null;
      originalMaterialRef.current = null;

      // Dispose existing preview completely to force recreation
      disposePreview();
    }

    // Preview mesh (this will create fresh snapshots if needed)
    ensurePreview();

    // Ring setup
    const grp = ensureRingGroup();
    const startY = initialRingYWorld;
    syncRingAt(startY);

    // Transform controls
    const tc = new TransformControls(camera, renderer?.domElement);
    tc.setMode('translate');
    tc.showX = false;
    tc.showZ = false;
    tc.showY = true;

    tc.addEventListener('dragging-changed', (e) => {
      if (controls) controls.enabled = !e.value;
    });

    // Use current shadedMesh reference, not closure capture
    tc.addEventListener('objectChange', () => {
      if (!ringGroupRef.current) return;
      const y = ringGroupRef.current.position.y;
      const currentMesh = useEditorStore.getState().sceneRefs.shadedMesh;

      // Update ring with current mesh
      if (currentMesh) {
        // Use current mesh for ring calculations
        const pos = currentMesh.geometry.attributes.position;
        const index = currentMesh.geometry.index ? currentMesh.geometry.index.array : null;
        const mw = currentMesh.matrixWorld;

        syncRingAt(y);
        rebuildPreview();
      }
    });

    const helper = tc.getHelper();
    helper.name = 'Elongation_TC_Helper';
    scene.add(helper);
    tc.attach(grp);
    tcRef.current = tc;
    tcHelperRef.current = helper;

    // Set initial visibility based on current state
    const isEnabled = useElongationStore.getState().enabled;
    if(previewMesh) previewMesh.visible = isEnabled;
    if(grp) grp.visible = isEnabled;
    if(helper) helper.visible = isEnabled;

    rebuildPreview();

    return () => {
      // Clean up THREE.js objects when dependencies change (but don't apply)

      if (tcRef.current) {
        tcRef.current.detach();
        tcRef.current.dispose?.();
        tcRef.current = null;
      }
      if (tcHelperRef.current) {
        scene.remove(tcHelperRef.current);
        tcHelperRef.current.parent?.remove(tcHelperRef.current);
        tcHelperRef.current = null;
      }

      disposeRingGroup();
    };
  }, [scene, shadedMesh]);

  // Rebuild preview when user changes the elongation value (mm)
  useEffect(() => {
    if (!scene || !shadedMesh) return;
    rebuildPreview();
  }, [value]);


  // Only show ring/controls when tool is enabled; otherwise still keep preview live
  useEffect(() => {
    if (!scene) return;
    if (ringGroupRef.current) ringGroupRef.current.visible = enabled;
    if (tcHelperRef.current) tcHelperRef.current.visible = enabled;
  }, [enabled]);

  // Preview mesh visibility follows enabled state
  useEffect(() => {
    if (!previewMesh) return;
    previewMesh.visible = !!enabled;
  }, [enabled, previewMesh]);

  // Adjust preview mesh material based on ghost mode
  useEffect(() => {
    const mesh = previewMesh || previewMeshRef.current;

    // Only update if preview mesh exists
    if (!mesh || !mesh.material) return;

    if (ghostModeEnabled) {
      // Ghost mode ON: Make preview completely opaque
      mesh.material.opacity = 1.0;
      mesh.material.transparent = false;
    } else {
      // Ghost mode OFF: Make preview transparent
      mesh.material.opacity = 0.5;
      mesh.material.transparent = true;
    }

    mesh.material.needsUpdate = true;
  }, [ghostModeEnabled, previewMesh]);

  return null;
};

export default ElongationHandler;


